import styles from './PricesPreview.module.scss';

import { Theme, useMediaQuery } from '@mui/material';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { useSinglePriceWithContext } from '@work4all/data';

import { PurchasePrice } from '@work4all/models/lib/Classes/PurchasePrice.entity';

import { formatNumberAsCurrency } from '@work4all/utils';
import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

export interface PurchasePricePreviewProps {
  purchasePrices: PurchasePrice[];
}

export function PurchasePricePreview(props: PurchasePricePreviewProps) {
  const { purchasePrices } = props;
  const { t } = useTranslation();

  const options = useSinglePriceWithContext();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );

  if (!purchasePrices || purchasePrices.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx(styles.container, styles.purchasePriceGrid, {
        [styles.mobile]: isMobile,
        [styles.desktop]: !isMobile,
      })}
    >
      {/* Header */}
      <div className={styles.headerCell}>{t('COMMON.SUPPLIER')}</div>
      <div className={styles.headerCell}>{t('MASK.VALID_FROM')}</div>
      <div className={clsx(styles.headerCell, styles.alignRight)}>
        {t('COMMON.QUANTITY_ABBREV')}
      </div>
      <div className={clsx(styles.headerCell, styles.alignRight)}>
        {t('MASK.PURCHASE_PRICE_ABBREV')}
      </div>
      <div className={clsx(styles.headerCell, styles.lastColumn)}>
        {t('COMMON.CURRENCY_ABBREV')}
      </div>

      {/* Body */}
      {purchasePrices.map((purchasePrice, index) => (
        <>
          <div
            key={`supplier-${index}`}
            className={clsx(styles.bodyCell, {
              [styles.withBottomBorder]: index < purchasePrices.length - 1,
            })}
          >
            {purchasePrice.supplier?.name || '-'}
          </div>
          <div
            key={`valid-from-${index}`}
            className={clsx(styles.bodyCell, {
              [styles.withBottomBorder]: index < purchasePrices.length - 1,
            })}
          >
            {DateTime.fromISO(purchasePrice.validFromDate).isValid
              ? DateTime.fromISO(purchasePrice.validFromDate).toLocaleString(
                  DateTimeCustom.DATE_SHORT
                )
              : ''}
          </div>
          <div
            key={`quantity-${index}`}
            className={clsx(styles.bodyCell, styles.alignRight, {
              [styles.withBottomBorder]: index < purchasePrices.length - 1,
            })}
          >
            1
          </div>
          <div
            key={`purchase-price-${index}`}
            className={clsx(styles.bodyCell, styles.alignRight, {
              [styles.withBottomBorder]: index < purchasePrices.length - 1,
            })}
          >
            {purchasePrice.purchasePrice
              ? formatNumberAsCurrency(purchasePrice.purchasePrice, options)
              : '-'}
          </div>
          <div
            key={`currency-${index}`}
            className={clsx(styles.bodyCell, styles.lastColumn, {
              [styles.withBottomBorder]: index < purchasePrices.length - 1,
            })}
          >
            {purchasePrice.currency?.name || 'EUR'}
          </div>
        </>
      ))}
    </div>
  );
}
